/**
 * Download helpers that save a file under a name of our choosing.
 *
 * A plain `<a href download="name">` is ignored for cross-origin URLs — the
 * uploads are served from the API's host, not the admin's — so the browser
 * would save the server's own file name. Fetching the file into a blob and
 * downloading the blob's object URL is what makes the name stick.
 */

const MIME_EXTENSIONS: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
};

/** Extension of a URL's file name ("" if it has none). Handles data-URLs. */
export const fileExtensionFromUrl = (url: string): string => {
  if (!url) return "";
  const dataMatch = /^data:([^;,]+)/i.exec(url);
  if (dataMatch) return MIME_EXTENSIONS[dataMatch[1].toLowerCase()] || "";
  const clean = url.split(/[?#]/)[0];
  const lastSegment = clean.split("/").pop() || "";
  const dot = lastSegment.lastIndexOf(".");
  return dot > 0 ? lastSegment.slice(dot + 1).toLowerCase() : "";
};

/**
 * `<ack>` or `<ack>_<Suffix>` — the naming used for every PAN download
 * (`910364200209262.pdf`, `910364200209262_Photo.png`, ...). Falls back to
 * `fallback` when the application has no acknowledgement number yet.
 */
export const ackFileBaseName = (
  acknowledgementNumber: string | undefined | null,
  suffix = "",
  fallback = "download"
): string => {
  const ack = String(acknowledgementNumber || "").trim();
  if (!ack) return suffix ? `${fallback}_${suffix}` : fallback;
  return suffix ? `${ack}_${suffix}` : ack;
};

/**
 * Fetch `url` and save it as `<baseName>.<ext>`. The extension comes from the
 * URL, then the response's content type, then `fallbackExt`.
 *
 * Returns false (after logging) when the file could not be fetched, so the
 * caller can show its own message.
 */
export const downloadFileAs = async (
  url: string,
  baseName: string,
  fallbackExt = ""
): Promise<boolean> => {
  if (!url) return false;
  try {
    const response = await fetch(url, { method: "GET" });
    if (!response.ok) throw new Error("Failed to fetch the file.");
    const blob = await response.blob();

    const ext =
      fileExtensionFromUrl(url) ||
      MIME_EXTENSIONS[(blob.type || "").split(";")[0].toLowerCase()] ||
      fallbackExt;
    const fileName = ext ? `${baseName}.${ext}` : baseName;

    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    return false;
  }
};

/** Save `blob` as `fileName` through a temporary object URL. */
export const saveBlob = (blob: Blob, fileName: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

/**
 * Stamp `dpi` into a JPEG's JFIF (APP0) header. Canvas encoders write 72 DPI
 * (or "no units"), or leave the header out altogether — in which case one is
 * inserted right after the SOI marker.
 */
const setJpegDpi = async (jpg: Blob, dpi: number): Promise<Blob> => {
  const bytes = new Uint8Array(await jpg.arrayBuffer());
  const hasJfif =
    bytes[2] === 0xff &&
    bytes[3] === 0xe0 &&
    String.fromCharCode(...Array.from(bytes.slice(6, 11))) === "JFIF\0";
  if (hasJfif) {
    bytes[13] = 1; // units: dots per inch
    bytes[14] = (dpi >> 8) & 0xff;
    bytes[15] = dpi & 0xff;
    bytes[16] = (dpi >> 8) & 0xff;
    bytes[17] = dpi & 0xff;
    return new Blob([bytes], { type: "image/jpeg" });
  }
  // prettier-ignore
  const app0 = new Uint8Array([
    0xff, 0xe0, 0x00, 0x10, 0x4a, 0x46, 0x49, 0x46, 0x00, 0x01, 0x01, 0x01,
    (dpi >> 8) & 0xff, dpi & 0xff, (dpi >> 8) & 0xff, dpi & 0xff, 0x00, 0x00,
  ]);
  return new Blob([bytes.slice(0, 2), app0, bytes.slice(2)], {
    type: "image/jpeg",
  });
};

/**
 * Pixel / byte targets for the PAN `<ack>_Photo` and `<ack>_Sig` downloads, as
 * the PAN portals take them, both 200 DPI: the photo exactly 204 x 204, the
 * whole photo stretched to fill it (no white strips, face not cut); the
 * signature exactly 333 x 137, the whole signature stretched to fill it — no
 * white strips at the sides, and no stroke is cut off.
 */
export const PAN_PHOTO_SPEC = {
  dpi: 200,
  maxBytes: 9 * 1024,
  exactSize: { width: 204, height: 204 },
  exactFit: "fill" as ExactFit,
};
export const PAN_SIG_SPEC = {
  dpi: 200,
  maxBytes: 7 * 1024,
  exactSize: { width: 333, height: 137 },
  exactFit: "fill" as ExactFit,
};

type Size = { width: number; height: number };
/**
 * How an image meets an `exactSize`: "cover" fills and crops, "contain" fits
 * inside and pads white, "fill" stretches to the box.
 */
export type ExactFit = "cover" | "contain" | "fill";

/**
 * Where `bitmap` goes on the output canvas.
 *
 * - `exactSize`: the canvas is exactly that size; with `exactFit` "cover"
 *   (default) the image fills it (`coverLayout`) — no white bars beside a
 *   portrait photo; with "contain" all of it is fitted inside and centred, the
 *   rest white (`containLayout`); with "fill" it is stretched to the box.
 * - `maxSize`: the canvas is the image scaled down to fit inside the box,
 *   never up.
 * - neither: the image's own size.
 *
 * `shrink` (<= 1) scales the result down further; only used without
 * `exactSize`, whose size must hold.
 */
type Layout = { canvas: Size; x: number; y: number; w: number; h: number };

/**
 * `bitmap` scaled to cover `size` (aspect kept) and cropped to it: centred
 * across, kept from the top down, so a tall passport photo loses chest, not
 * the top of the head. The overflow is simply drawn off the canvas.
 */
const coverLayout = (bitmap: { width: number; height: number }, size: Size): Layout => {
  const scale = Math.max(size.width / bitmap.width, size.height / bitmap.height);
  const w = Math.max(size.width, Math.round(bitmap.width * scale));
  const h = Math.max(size.height, Math.round(bitmap.height * scale));
  return { canvas: size, x: Math.round((size.width - w) / 2), y: 0, w, h };
};

/** `bitmap` fitted inside `size` (aspect kept, enlarged if smaller), centred. */
const containLayout = (bitmap: { width: number; height: number }, size: Size): Layout => {
  const scale = Math.min(size.width / bitmap.width, size.height / bitmap.height);
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  return {
    canvas: size,
    x: Math.round((size.width - w) / 2),
    y: Math.round((size.height - h) / 2),
    w,
    h,
  };
};

const exactLayout = (
  bitmap: { width: number; height: number },
  size: Size,
  fit: ExactFit = "cover"
): Layout => {
  if (fit === "fill") return { canvas: size, x: 0, y: 0, w: size.width, h: size.height };
  return fit === "contain" ? containLayout(bitmap, size) : coverLayout(bitmap, size);
};

const layoutFor = (
  bitmap: ImageBitmap,
  exactSize?: Size,
  maxSize?: Size,
  shrink = 1,
  exactFit?: ExactFit
): Layout => {
  if (exactSize) return exactLayout(bitmap, exactSize, exactFit);
  const fit = maxSize
    ? Math.min(1, maxSize.width / bitmap.width, maxSize.height / bitmap.height)
    : 1;
  const w = Math.max(1, Math.round(bitmap.width * fit * shrink));
  const h = Math.max(1, Math.round(bitmap.height * fit * shrink));
  return { canvas: { width: w, height: h }, x: 0, y: 0, w, h };
};

/** Draw `bitmap` per `layout` on a white canvas and encode it as a JPEG. */
const encodeOnWhite = (
  bitmap: ImageBitmap,
  layout: Layout,
  quality: number
): Promise<Blob | null> => {
  const canvas = document.createElement("canvas");
  canvas.width = layout.canvas.width;
  canvas.height = layout.canvas.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available.");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, layout.x, layout.y, layout.w, layout.h);
  return new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality)
  );
};

/**
 * The best-quality JPEG of `bitmap` that fits in `maxBytes`: the quality is
 * binary-searched, and only if even the lowest acceptable quality is too big
 * is the image scaled down (10% at a time) and searched again. With
 * `exactSize` the pixel size is fixed, so only the quality moves — down to a
 * lower floor.
 */
const encodeWithinSize = async (
  bitmap: ImageBitmap,
  maxBytes: number,
  exactSize?: Size,
  maxSize?: Size,
  exactFit?: ExactFit
): Promise<Blob> => {
  const MIN_QUALITY = exactSize ? 0.2 : 0.4;
  for (let shrink = 1; shrink > 0.05; shrink *= 0.9) {
    const layout = layoutFor(bitmap, exactSize, maxSize, shrink, exactFit);
    let lo = MIN_QUALITY;
    let hi = 0.95;
    let best: Blob | null = null;
    for (let i = 0; i < 7; i++) {
      const q = (lo + hi) / 2;
      const jpg = await encodeOnWhite(bitmap, layout, q);
      if (!jpg) throw new Error("Could not encode the image.");
      if (jpg.size <= maxBytes) {
        best = jpg;
        lo = q;
      } else {
        hi = q;
      }
    }
    if (!best) {
      const floor = await encodeOnWhite(bitmap, layout, MIN_QUALITY);
      if (floor && floor.size <= maxBytes) best = floor;
    }
    if (best) return best;
    if (exactSize) break;
  }
  throw new Error(`Could not fit the image in ${maxBytes} bytes.`);
};

/**
 * Download an image with any transparency filled WHITE, as a JPG.
 *
 * The signature edited in the PAN form is stored as a transparent PNG — it has
 * to be, since the form also prints it as the cross sign over the photograph.
 * Downloaded as-is, though, the see-through paper shows up black or as a
 * checkerboard in most viewers and upload portals. So the download is put on
 * white. An image that cannot be transparent (a JPG) is saved untouched —
 * unless `options` asks for a DPI or a size cap, which means re-encoding it.
 *
 * `options.dpi` is written into the JPEG header; `options.maxBytes` keeps the
 * file at or under that size; `options.exactSize` / `options.maxSize` set the
 * pixel size (see `layoutFor`). PAN_PHOTO_SPEC / PAN_SIG_SPEC hold the values
 * the PAN portals take.
 */
export const downloadImageOnWhite = async (
  url: string,
  baseName: string,
  options: {
    dpi?: number;
    maxBytes?: number;
    exactSize?: Size;
    exactFit?: ExactFit;
    maxSize?: Size;
  } = {}
): Promise<boolean> => {
  if (!url) return false;
  try {
    const response = await fetch(url, { method: "GET" });
    if (!response.ok) throw new Error("Failed to fetch the file.");
    const blob = await response.blob();
    const type = (blob.type || "").split(";")[0].toLowerCase();
    const urlExt = fileExtensionFromUrl(url);
    const mayBeTransparent =
      ["image/png", "image/webp", "image/gif"].includes(type) ||
      ["png", "webp", "gif"].includes(urlExt);
    const reencode = Boolean(
      options.dpi || options.maxBytes || options.exactSize || options.maxSize
    );
    if (!mayBeTransparent && !reencode)
      return downloadFileAs(url, baseName, "jpg");

    // Read from the blob, so the canvas is never tainted by a cross-origin URL.
    const bitmap = await createImageBitmap(blob);
    let jpg: Blob | null;
    try {
      jpg = options.maxBytes
        ? // Headroom for the JFIF header `setJpegDpi` may have to add.
          await encodeWithinSize(
            bitmap,
            options.maxBytes - (options.dpi ? 18 : 0),
            options.exactSize,
            options.maxSize,
            options.exactFit
          )
        : await encodeOnWhite(
            bitmap,
            layoutFor(bitmap, options.exactSize, options.maxSize, 1, options.exactFit),
            0.95
          );
    } finally {
      bitmap.close();
    }
    if (!jpg) throw new Error("Could not encode the image.");
    if (options.dpi) jpg = await setJpegDpi(jpg, options.dpi);

    saveBlob(jpg, `${baseName}.jpg`);
    return true;
  } catch (error) {
    console.error("Error downloading image:", error);
    return false;
  }
};

/**
 * Resize `src` (a data-URL or a remote URL) to EXACTLY `width` x `height` and
 * encode it as a JPEG on white. With a matching pair (aspect ratio kept) that
 * is a plain resize; otherwise `fit` decides: "cover" (default) crops it to
 * fill the box — a portrait photo at 204 x 204 keeps its face shape —
 * "contain" fits all of it inside, padded white, and "fill" stretches it to
 * the box (a signature: no white strips, no stroke cut off).
 *
 * `maxBytes` binary-searches the JPEG quality down to 0.2 to fit; the pixel
 * size never changes, so an image that still does not fit throws and the
 * operator lowers the pixels or raises the limit. `dpi` is written into the
 * JFIF header.
 */
export const resizeImageToJpeg = async (
  src: string,
  options: { width: number; height: number; maxBytes?: number; dpi?: number; fit?: ExactFit }
): Promise<Blob> => {
  const width = Math.max(1, Math.round(options.width));
  const height = Math.max(1, Math.round(options.height));
  const response = await fetch(src, { method: "GET" });
  if (!response.ok) throw new Error("Failed to fetch the image.");
  // Read from the blob, so the canvas is never tainted by a cross-origin URL.
  const bitmap = await createImageBitmap(await response.blob());
  const layout = exactLayout(bitmap, { width, height }, options.fit);
  // Headroom for the JFIF header `setJpegDpi` may have to add.
  const limit = options.maxBytes
    ? options.maxBytes - (options.dpi ? 18 : 0)
    : Infinity;
  let jpg: Blob | null = null;
  try {
    if (limit === Infinity) {
      jpg = await encodeOnWhite(bitmap, layout, 0.95);
    } else {
      let lo = 0.2;
      let hi = 0.95;
      for (let i = 0; i < 8; i++) {
        const q = (lo + hi) / 2;
        const candidate = await encodeOnWhite(bitmap, layout, q);
        if (!candidate) throw new Error("Could not encode the image.");
        if (candidate.size <= limit) {
          jpg = candidate;
          lo = q;
        } else {
          hi = q;
        }
      }
      if (!jpg) {
        const floor = await encodeOnWhite(bitmap, layout, 0.2);
        if (floor && floor.size <= limit) jpg = floor;
      }
    }
  } finally {
    bitmap.close();
  }
  if (!jpg) {
    throw new Error(
      `${width} x ${height} px does not fit in ${Math.round(
        (options.maxBytes || 0) / 1024
      )} KB. Lower the pixels or raise the KB.`
    );
  }
  return options.dpi ? setJpegDpi(jpg, options.dpi) : jpg;
};
